import React, { useState } from 'react';
import { emailPurchaseOrderToRecipients } from '../../services/purchaseOrderService';
import useHideMobileNav from '../../utils/useHideMobileNav';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export default function EmailToAnyoneModal({ purchaseOrder, onClose, onSent }) {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useHideMobileNav(!!purchaseOrder);

  if (!purchaseOrder) return null;

  const addEmailFromDraft = () => {
    const cleaned = draft.trim().toLowerCase();
    if (!cleaned) return;
    if (!isValidEmail(cleaned)) {
      setError(`"${draft.trim()}" doesn't look like an email address.`);
      return;
    }
    if (emails.includes(cleaned)) {
      setDraft('');
      return;
    }
    setEmails((prev) => [...prev, cleaned]);
    setDraft('');
    setError('');
  };

  const removeEmail = (email) => setEmails((prev) => prev.filter((e) => e !== email));

  const handleSend = async () => {
    if (!emails.length) {
      setError('Add at least one recipient.');
      return;
    }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const result = await emailPurchaseOrderToRecipients(purchaseOrder.id, emails);
      setSuccess(result?.message || `Sent to ${emails.length} recipient${emails.length === 1 ? '' : 's'}.`);
      if (typeof onSent === 'function') onSent(result);
    } catch (err) {
      setError(err?.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Email purchase order to anyone">
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, color: '#3D2518', fontSize: 18 }}>
              Email PO {purchaseOrder.po_number}
            </div>
            <div style={{ color: '#8B7B73', fontSize: 13, marginTop: 4 }}>
              Send a copy of this purchase order to any email addresses. The vendor record is unchanged.
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">×</button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <label style={labelStyle}>Recipients</label>
          <div style={chipRowStyle}>
            {emails.map((email) => (
              <span key={email} style={chipStyle}>
                {email}
                <button type="button" onClick={() => removeEmail(email)} style={chipRemoveStyle} aria-label={`Remove ${email}`}>×</button>
              </span>
            ))}
            <input
              type="email"
              placeholder={emails.length ? 'Add another…' : 'name@example.com'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                  e.preventDefault();
                  addEmailFromDraft();
                } else if (e.key === 'Backspace' && !draft && emails.length) {
                  setEmails((prev) => prev.slice(0, -1));
                }
              }}
              onBlur={addEmailFromDraft}
              style={chipInputStyle}
              disabled={sending}
            />
          </div>

          {error && <div style={errorStyle}>{error}</div>}
          {success && <div style={successStyle}>{success}</div>}
        </div>

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={cancelBtnStyle} disabled={sending}>Close</button>
          <button type="button" onClick={handleSend} style={primaryBtnStyle} disabled={sending || !emails.length}>
            {sending ? 'Sending…' : `Send to ${emails.length || 0} recipient${emails.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  paddingLeft: 16, paddingRight: 16,
};
const modalStyle = {
  width: '100%', maxWidth: 520, background: '#fff', borderRadius: 14,
  boxShadow: '0 24px 48px rgba(0,0,0,0.2)', overflow: 'hidden', fontFamily: 'Open Sans, sans-serif',
};
const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  padding: '18px 20px 8px', borderBottom: '1px solid #f0e6dd',
};
const closeBtnStyle = {
  border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, color: '#8B7B73', cursor: 'pointer',
};
const labelStyle = {
  display: 'block', fontWeight: 700, color: '#5A4A42', fontSize: 12,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
};
const chipRowStyle = {
  display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, border: '1.5px solid #E9DFD6',
  borderRadius: 10, background: '#FAF6F2', minHeight: 48,
};
const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
  background: '#fff', border: '1px solid #d8cdc3', borderRadius: 999, fontSize: 13, color: '#3D2518',
};
const chipRemoveStyle = {
  background: 'transparent', border: 'none', color: '#8B7B73', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1,
};
const chipInputStyle = {
  flex: 1, minWidth: 160, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#3D2518',
};
const footerStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid #f0e6dd', background: '#fdfaf6',
};
const cancelBtnStyle = {
  background: '#fff', border: '1px solid #d8cdc3', color: '#5A4A42', borderRadius: 10, padding: '8px 14px', fontWeight: 600, cursor: 'pointer',
};
const primaryBtnStyle = {
  background: '#526B7C', border: '1px solid #3F5563', color: '#fff', borderRadius: 10, padding: '8px 16px', fontWeight: 700, cursor: 'pointer',
};
const errorStyle = { color: '#b71c1c', background: '#fdecea', padding: '8px 12px', borderRadius: 8, marginTop: 12, fontSize: 13 };
const successStyle = { color: '#1b5e20', background: '#e8f5e9', padding: '8px 12px', borderRadius: 8, marginTop: 12, fontSize: 13 };
